import React, { useCallback, useState } from 'react';
import { Brain, Plus, CheckCircle2 } from 'lucide-react';
import SectionShell from '../plan/SectionShell';

/**
 * HPToolsInFlight — during-perform check-off board.
 *
 * Data flow (append-only audit trail):
 *   1. `draft.hp_tools` (from Plan phase) → list of *available* tools.
 *   2. Each "Use now" click → POSTs a `hp_tool_used` timeline event.
 *   3. `performState.hp_tool_uses` → per-tool `count` + `last_used_at`.
 *   4. No local state — reload is driven from parent via `onLogged`.
 */
export default function HPToolsInFlight({ draft, performState, ownerEmail, onLogged, isReadOnly }) {
  const tools = draft?.hp_tools || [];
  const usageByToolId = React.useMemo(() => {
    const m = {};
    for (const u of performState?.hp_tool_uses || []) m[u.tool_id] = u;
    return m;
  }, [performState]);
  const [noteFor, setNoteFor] = useState({});
  const [busy, setBusy] = useState('');

  const use = useCallback(async (tool) => {
    if (!ownerEmail) return;
    setBusy(tool.tool_id);
    try {
      await onLogged?.('hp_tool_used', {
        tool_id:    tool.tool_id,
        tool_label: tool.label,
        notes:      (noteFor[tool.tool_id] || '').trim() || undefined,
      });
      setNoteFor((n) => ({ ...n, [tool.tool_id]: '' }));
    } finally {
      setBusy('');
    }
  }, [ownerEmail, noteFor, onLogged]);

  return (
    <SectionShell
      icon={Brain}
      title="HP Tools In-Flight"
      subtitle="Every use is recorded to the immutable audit log. Tap a tool the moment you apply it."
      color="#86efac"
      badge={tools.length ? `${tools.length} loaded` : 'None from Plan'}
      testId="jbv2-section-hp-inflight"
    >
      {tools.length === 0 && (
        <div className="jbv2-empty" data-testid="jbv2-hp-inflight-empty">
          No HP tools have been acknowledged in the Plan phase yet. Return to Plan → HP Tools to add them,
          then this board becomes your live check-off log.
        </div>
      )}
      {tools.map((t) => {
        const use_ = usageByToolId[t.tool_id];
        const count = use_?.count || 0;
        return (
          <div key={t.tool_id} className="jbv2-row jbv2-hp-row" data-testid={`jbv2-hp-inflight-row-${t.tool_id}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <div className="jbv2-row-cat">{t.label}</div>
                {t.when && <div className="jbv2-hp-row-meta"><strong>Use when:</strong> {t.when}</div>}
                <div className="jbv2-hp-row-meta" style={{ marginTop: 4 }}>
                  <strong>Used:</strong>{' '}
                  <span data-testid={`jbv2-hp-inflight-count-${t.tool_id}`}>{count}</span>
                  {' '}time{count === 1 ? '' : 's'} this session
                  {use_?.last_note && <> · last note: <em>&ldquo;{use_.last_note}&rdquo;</em></>}
                </div>
              </div>
              {!isReadOnly && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    className="jbv2-field-input"
                    style={{ maxWidth: 220, padding: '6px 9px', fontSize: 12.5 }}
                    placeholder="Optional note"
                    value={noteFor[t.tool_id] || ''}
                    onChange={(e) => setNoteFor((n) => ({ ...n, [t.tool_id]: e.target.value }))}
                    data-testid={`jbv2-hp-inflight-note-${t.tool_id}`}
                  />
                  <button
                    type="button"
                    className="jbv2-row-btn"
                    onClick={() => use(t)}
                    disabled={busy === t.tool_id}
                    data-testid={`jbv2-hp-inflight-use-${t.tool_id}`}
                  >
                    <CheckCircle2 size={12} /> Log use
                  </button>
                </div>
              )}
            </div>
            {t.citation && (
              <span className="jbv2-hp-row-cite" style={{ alignSelf: 'flex-start' }}>
                Source: {t.citation.source_title}
              </span>
            )}
          </div>
        );
      })}
    </SectionShell>
  );
}
